/**
 * External dependencies
 */
import PropTypes from 'prop-types';
import styled from 'styled-components';
import { __, sprintf } from '@web-stories-wp/i18n';
import {
  BUTTON_TRANSITION_TIMING,
  Icons,
  Text,
  THEME_CONSTANTS,
  themeHelpers,
} from '@web-stories-wp/design-system';

/**
 * Internal dependencies
 */
import { focusStyle } from '../../../panels/shared';
import { formatAnimationMetadata } from './utils';

const CardItem = styled.div`
  min-width: 0;
`;

const CardButton = styled.button`
  display: flex;
  flex-direction: column;
  gap: 8px;
  width: 100%;
  min-width: 0;
  border: 0;
  border-radius: ${({ theme }) => theme.borders.radius.small};
  padding: 8px;
  background: ${({ theme }) => theme.colors.interactiveBg.previewOverlay};
  color: ${({ theme }) => theme.colors.fg.primary};
  cursor: pointer;
  text-align: left;
  transition: background-color ${BUTTON_TRANSITION_TIMING};

  &:hover,
  &:focus {
    background: ${({ theme }) => theme.colors.interactiveBg.secondaryHover};
  }

  &:disabled {
    cursor: wait;
    opacity: 0.72;
  }

  ${({ isActive }) => isActive && themeHelpers.focusCSS};
  ${focusStyle};
`;

const Preview = styled.div`
  position: relative;
  width: 100%;
  aspect-ratio: 3 / 4;
  min-height: 112px;
  overflow: hidden;
  border-radius: ${({ theme }) => theme.borders.radius.small};
  background: ${({ theme }) => theme.colors.bg.secondary};
`;

const PreviewImage = styled.img`
  display: block;
  width: 100%;
  height: 100%;
  object-fit: contain;
`;

const Placeholder = styled.div`
  display: flex;
  width: 100%;
  height: 100%;
  align-items: center;
  justify-content: center;
  color: ${({ theme }) => theme.colors.fg.secondary};

  svg {
    width: 32px;
    height: 32px;
  }
`;

const PendingOverlay = styled.div`
  position: absolute;
  inset: 0;
  display: flex;
  align-items: center;
  justify-content: center;
  background: ${({ theme }) => theme.colors.opacity.overlayDark};
`;

const Title = styled(Text).attrs({
  size: THEME_CONSTANTS.TYPOGRAPHY.PRESET_SIZES.SMALL,
})`
  width: 100%;
  overflow: hidden;
  text-overflow: ellipsis;
  white-space: nowrap;
`;

const Metadata = styled(Text).attrs({
  size: THEME_CONSTANTS.TYPOGRAPHY.PRESET_SIZES.X_SMALL,
})`
  width: 100%;
  color: ${({ theme }) => theme.colors.fg.secondary};
  overflow: hidden;
  text-overflow: ellipsis;
  white-space: nowrap;
`;

function LottieCard({ animation, isPending, onInsert }) {
  const title = animation.title || __('Untitled animation', 'web-stories');
  const metadata = formatAnimationMetadata(animation);

  return (
    <CardItem role="listitem">
      <CardButton
        type="button"
        aria-label={sprintf(
          /* translators: %s: animation title. */
          __('Insert %s Lottie animation', 'web-stories'),
          title
        )}
        disabled={isPending}
        onClick={() => onInsert(animation)}
      >
        <Preview>
          {animation.previewImageUrl ? (
            <PreviewImage src={animation.previewImageUrl} alt="" />
          ) : (
            <Placeholder>
              <Icons.Timeline aria-hidden />
            </Placeholder>
          )}
          {isPending && (
            <PendingOverlay>
              <Metadata forwardedAs="span">
                {__('Adding...', 'web-stories')}
              </Metadata>
            </PendingOverlay>
          )}
        </Preview>
        <Title>{title}</Title>
        {metadata && <Metadata>{metadata}</Metadata>}
      </CardButton>
    </CardItem>
  );
}

LottieCard.propTypes = {
  animation: PropTypes.shape({
    id: PropTypes.string,
    title: PropTypes.string,
    previewImageUrl: PropTypes.string,
    width: PropTypes.number,
    height: PropTypes.number,
    duration: PropTypes.number,
    fps: PropTypes.number,
  }).isRequired,
  isPending: PropTypes.bool,
  onInsert: PropTypes.func.isRequired,
};

export default LottieCard;
